import type { FigmaColor } from './semantic-colors'
import { semanticColors } from './semantic-colors'
import { semanticModes, semanticTokens } from './semantic-tokens'

export type FigmaMode = 'base-ui' | 'finavia' | 'swedavia' | 'tallinn'

export type FigmaBinding = {
    value?: string | number
    boundTo?: string
    boundToCollection?: string
}

export type FigmaToken = FigmaBinding & {
    alias: string
    type: string
    id: string
    desc?: string
    scopes?: string[]
    modes?: Record<FigmaMode, FigmaBinding>
}

export const figmaTokens = semanticTokens as unknown as Record<string, FigmaToken>

export const FIGMA_MODES = Object.keys(semanticModes) as FigmaMode[]

export const figmaModeName = (mode: FigmaMode): string =>
    (semanticModes as Record<string, string>)[mode]

/**
 * The mode these helpers resolve against when a caller does not name one. There is
 * deliberately no default: every alias exists in every mode, so guessing wrong
 * returns another tenant's colour silently rather than failing. The app sets this
 * once at startup from its build tenant.
 */
let activeMode: FigmaMode | undefined

export const figmaMode = (): FigmaMode | undefined => activeMode

export const setFigmaMode = (mode: FigmaMode): void => {
    activeMode = mode
}

const requireMode = (mode: FigmaMode | undefined): FigmaMode => {
    if (!mode) {
        throw new Error(
            'figma tokens: no mode given and none set. Pass the mode explicitly, or call ' +
                "setFigmaMode() once at startup with the build tenant's mode " +
                '(FIGMA_MODE_BY_TENANT in figma-tailwind.ts).',
        )
    }
    return mode
}

const MAX_BINDING_DEPTH = 8

export const bindingFor = (token: FigmaToken, mode: FigmaMode): FigmaBinding =>
    token.modes ? token.modes[mode] : token

export type BindingStep = {
    alias: string
    boundTo?: string
    boundToCollection?: string
    value?: string | number
}

export const bindingChain = (alias: string, mode: FigmaMode): BindingStep[] => {
    const steps: BindingStep[] = []
    let current = alias
    for (let depth = 0; depth <= MAX_BINDING_DEPTH; depth += 1) {
        const token = figmaTokens[current]
        if (!token) {
            return steps
        }
        const binding = bindingFor(token, mode)
        steps.push({ alias: current, ...binding })
        if (!binding.boundTo || binding.boundToCollection) {
            return steps
        }
        current = binding.boundTo
    }
    throw new Error(`figma tokens: binding chain from "${alias}" exceeds ${MAX_BINDING_DEPTH} hops`)
}

export const valueFor = (alias: string, mode: FigmaMode): string | number | undefined => {
    const steps = bindingChain(alias, mode)
    return steps[steps.length - 1]?.value
}

export const tokenGroup = (alias: string): string => alias.split('/')[0]

export const tokensByGroup = (): Record<string, string[]> =>
    Object.keys(figmaTokens).reduce<Record<string, string[]>>((groups, alias) => {
        const group = tokenGroup(alias)
        groups[group] = [...(groups[group] ?? []), alias]
        return groups
    }, {})

type ColorNode = FigmaColor | { [key: string]: ColorNode }

const isColorLeaf = (node: ColorNode): node is FigmaColor =>
    typeof (node as FigmaColor).value === 'string'

const flattenColors = (node: ColorNode, out: Record<string, FigmaColor> = {}) => {
    if (isColorLeaf(node)) {
        out[node.alias] = node
        return out
    }
    for (const child of Object.values(node)) {
        flattenColors(child, out)
    }
    return out
}

const COLORS_BY_MODE = Object.fromEntries(
    Object.entries(semanticColors as Record<string, ColorNode>).map(([mode, tree]) => [
        mode,
        flattenColors(tree),
    ]),
) as Record<FigmaMode, Record<string, FigmaColor>>

export const colorToken = (
    alias: string,
    mode: FigmaMode = requireMode(activeMode),
): FigmaColor => {
    const leaf = COLORS_BY_MODE[mode]?.[alias]
    if (!leaf) {
        throw new Error(`figma tokens: no colour "${alias}" in mode "${mode}"`)
    }
    return leaf
}

export const color = (alias: string, mode: FigmaMode = requireMode(activeMode)): string =>
    colorToken(alias, mode).value

export const colorsFor = (mode: FigmaMode = requireMode(activeMode)): Record<string, FigmaColor> =>
    COLORS_BY_MODE[mode]
